import { put, takeEvery, select } from 'redux-saga/effects'

import {
  GROW_MESSAGE_LIMIT,
  SET_CHAT_MESSAGES,
  SET_CHAT_REPLIES,
  SET_IS_AWAITING_MESSAGE,
  SET_IS_READY,
  SET_UNREAD_MESSAGE_INDEX
} from '@redux/actions/types'

/* DEV-5210: the chat thread should display 50 *top-level* messages. The
 * Firestore listener fetches a fixed window of raw docs which the saga
 * splits by responseId; only the no-responseId messages are rendered.
 * Empirically ~58% of traffic is top-level, so a 50-doc window yielded
 * only ~29 rendered. We fetch a buffered window and adaptively grow it
 * until 50 top-level messages are reached or we run out of headroom. */
export const INITIAL_MESSAGE_LIMIT = 100
export const MESSAGE_LIMIT_STEP = 50
export const MAX_MESSAGE_LIMIT = 300
export const TOP_LEVEL_TARGET = 50

const getChatIsReady = state => state.initialize.chatIsReady

const getHash = state => state.auth.hash

const getMessages = state => state.firestore.ordered[`chats/${state.auth.hash}/messages`]

const getChatMessages = state => state.chat.messages

const getChatResponses = state => state.chat.responseMessages

const getUnreadMessageIndex = state => state.chat.unreadMessageIndex

const getMessageLimit = state => state.chat.messageLimit

/**
 * Updates the unread message index based on a message's read status.
 * @param action
 */
function * setReadIndex(action) {
  const message = action.payload.data

  const rawMessages = yield select(getMessages)

  const messages = rawMessages?.slice().reverse() || []

  const messageInArray = messages.find(msg => msg.guid === message.guid)

  const index = messages.indexOf(messageInArray)

  if (!message.read) {
    yield put({
      type: SET_UNREAD_MESSAGE_INDEX,
      payload: index
    })
  } else {
    const unreadMessageIndex = yield select(getUnreadMessageIndex)

    if (index === unreadMessageIndex) {
      yield put({
        type: SET_UNREAD_MESSAGE_INDEX,
        payload: -1
      })
    }
  }
}

/**
 * Finds the first unread message and sets its index in the store.
 * @param root0
 * @param root0.messages
 */
function * setUnreadData({ messages: rawMessages }) {
  const messages = rawMessages?.slice().reverse() || []

  const firstUnreadMessage = messages.find(message => !message.read)

  const firstUnreadMessageIndex = messages.indexOf(firstUnreadMessage)

  yield put({
    type: SET_UNREAD_MESSAGE_INDEX,
    payload: firstUnreadMessageIndex
  })
}

/**
 * Handles a new Firestore chat document by appending it to messages or replies.
 * @param action
 */
function * onDocumentAdded(action) {
  const hash = yield select(getHash)

  const chatMessages = yield select(getChatMessages)

  const chatResponses = yield select(getChatResponses)

  const isChat = action.meta.collection === `chats/${hash}/messages`

  const documentId = action.meta.doc

  if (!isChat) return

  yield put({
    type: SET_IS_AWAITING_MESSAGE,
    payload: false
  })

  const document = action.payload.data

  const message = {
    id: documentId,
    ...document
  }

  if (message.responseId) {
    yield put({
      type: SET_CHAT_REPLIES,
      payload: [message, ...chatResponses]
    })
  } else {
    yield put({
      type: SET_CHAT_MESSAGES,
      payload: [message, ...chatMessages]
    })
    yield setReadIndex(action)
  }
}

/**
 * Updates an existing chat message in the store when its Firestore document is modified.
 * @param action
 */
function * onDocumentModified(action) {
  const hash = yield select(getHash)

  const isChat = action.meta.collection === `chats/${hash}/messages`

  if (!isChat) return

  const chatMessages = yield select(getChatMessages)

  const documentId = action.meta.doc

  const document = action.payload.data

  const message = {
    id: documentId,
    ...document
  }

  const updatedMessages = chatMessages.map(chatMessage => (chatMessage.id === documentId ? message : chatMessage))

  yield put({
    type: SET_CHAT_MESSAGES,
    payload: updatedMessages
  })

  yield setReadIndex(action)
}

/**
 * Processes the initial Firestore listener response to populate chat messages and replies.
 * @param action
 */
export function * onListenerResponse(action) {
  const hash = yield select(getHash)

  const isChat = action.meta.collection === `chats/${hash}/messages`

  if (!isChat) return

  const allMessages = action.payload.ordered

  const topLevelMessages = allMessages.filter(message => !message.responseId)

  const responseMessages = allMessages.filter(message => message.responseId)

  /* DEV-5210: cap the rendered list at 50 top-level. Newest first per
   * orderBy(createdAt, desc), so slice keeps the newest 50. */
  const messages = topLevelMessages.slice(0, TOP_LEVEL_TARGET)

  const chatIsReady = yield select(getChatIsReady)

  yield put({
    type: SET_CHAT_MESSAGES,
    payload: messages
  })

  yield put({
    type: SET_CHAT_REPLIES,
    payload: responseMessages
  })

  /* DEV-5210: if we under-filled the target AND the listener returned a
   * full window (i.e. there may be more docs the lookback hasn't seen),
   * grow the limit. The query rebinds and the listener re-emits with
   * the bigger window. Capped at MAX_MESSAGE_LIMIT to bound runaway
   * growth on response-heavy threads. */
  const currentLimit = yield select(getMessageLimit)

  if (
    topLevelMessages.length < TOP_LEVEL_TARGET &&
    allMessages.length === currentLimit &&
    currentLimit < MAX_MESSAGE_LIMIT
  ) {
    yield put({ type: GROW_MESSAGE_LIMIT })
  }

  if (!chatIsReady) {
    yield put({
      type: SET_IS_READY,
      payload: {
        chatIsReady: true
      }
    })
  }

  yield setUnreadData({ messages })
}

/**
 * Registers watchers for Firestore chat document events.
 */
function * watchChat() {
  yield takeEvery('@@reduxFirestore/DOCUMENT_ADDED', onDocumentAdded)
  yield takeEvery('@@reduxFirestore/DOCUMENT_MODIFIED', onDocumentModified)
  yield takeEvery('@@reduxFirestore/LISTENER_RESPONSE', onListenerResponse)
}

export default watchChat
